import React from 'react';
import styled from 'styled-components';
import { Input } from 'components/Form/Input';
import { SearchInput } from 'components/Form/SearchInput';
import { Select } from 'components/Form/Select';
import {
  PhoneInput,
  Select as ModernSelect,
  Textarea,
  Toggle,
  FormField,
  FormLabel,
  Tooltip,
} from 'components/FormFields';
import CodeBlock from '../../components/CodeBlock';
import ComponentDemo from '../../components/ComponentDemo';
import PropsTable from '../../components/PropsTable';

// No-op function for demo purposes
const noop = () => undefined;

const PageHeader = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 15px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.7;
  margin: 0 0 32px 0;
`;

const Forms = () => {
  const [inputValue, setInputValue] = React.useState('');
  const [emailValue, setEmailValue] = React.useState('');
  const [selectValue, setSelectValue] = React.useState('');
  const [modernSelectValue, setModernSelectValue] = React.useState('');
  // eslint-disable-next-line no-unused-vars
  const [searchValue, setSearchValue] = React.useState('');
  const [phoneValue, setPhoneValue] = React.useState('');
  const [countryCode, setCountryCode] = React.useState('+1');
  const [textareaValue, setTextareaValue] = React.useState('');
  const [toggleChecked, setToggleChecked] = React.useState(false);
  const [toggleChecked2, setToggleChecked2] = React.useState(true);

  // Note: Form components require theme context
  // They're wrapped in EnhancedThemeProvider by the StyleGuide parent

  const inputProps = [
    {
      name: 'label',
      type: 'string',
      required: false,
      description: 'Label text displayed above the input field',
    },
    {
      name: 'description',
      type: 'string',
      required: false,
      description: 'Helper text displayed below the input field',
    },
    {
      name: 'error',
      type: 'string',
      required: false,
      description: 'Error message to display below the input',
    },
    {
      name: 'icon',
      type: 'string',
      required: false,
      description:
        'Icon name to display inside the input (uses Icon component)',
    },
    {
      name: 'labelRight',
      type: 'node',
      required: false,
      description: 'Content to display on the right side of the label',
    },
    {
      name: 'disabled',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Disables the input field',
    },
    {
      name: 'readonly',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Makes the input read-only',
    },
    {
      name: 'value',
      type: 'string',
      required: false,
      description: 'Current value of the input',
    },
    {
      name: 'onChange',
      type: 'function',
      required: false,
      description: 'Change handler function',
    },
    {
      name: 'type',
      type: 'string',
      default: "'text'",
      required: false,
      description: 'Input type (text, email, password, number, etc.)',
    },
  ];

  const selectProps = [
    {
      name: 'label',
      type: 'string',
      required: false,
      description: 'Label text displayed above the select',
    },
    {
      name: 'description',
      type: 'string',
      required: false,
      description: 'Helper text displayed below the select',
    },
    {
      name: 'error',
      type: 'string',
      required: false,
      description: 'Error message to display',
    },
    {
      name: 'options',
      type: 'array',
      required: true,
      description: 'Array of {value, label} objects for select options',
    },
    {
      name: 'value',
      type: 'string',
      required: false,
      description: 'Currently selected value',
    },
    {
      name: 'placeholder',
      type: 'string',
      required: false,
      description: 'Placeholder text for empty state',
    },
    {
      name: 'variant',
      type: "'minimal' | undefined",
      required: false,
      description: 'Visual variant of the select (minimal has less padding)',
    },
    {
      name: 'onChange',
      type: 'function',
      required: false,
      description: 'Change handler function',
    },
  ];

  return (
    <div>
      <PageHeader>
        <Title>Forms</Title>
        <Subtitle>
          Form components for text inputs, selects, and search. All components
          support labels, descriptions, error states, validation, and dark mode.
          Use the modern FormFields components (Select, Input, PhoneInput) for
          new development.
        </Subtitle>
      </PageHeader>

      <Section>
        <SectionTitle>Text Input</SectionTitle>
        <SectionDescription>
          Basic text input component with support for labels, descriptions,
          icons, and error messages.
        </SectionDescription>

        <ComponentDemo label="Basic Input" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <Input
              label="Full Name"
              placeholder="Enter your name"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
          </div>
        </ComponentDemo>

        <ComponentDemo label="Input with Description" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <Input
              label="Email Address"
              description="We'll never share your email with anyone else"
              placeholder="email@example.com"
              type="email"
              value={emailValue}
              onChange={(e) => setEmailValue(e.target.value)}
            />
          </div>
        </ComponentDemo>

        <ComponentDemo label="Input with Error" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <Input
              label="Username"
              error="This username is already taken"
              placeholder="username"
              value="taken_username"
              onChange={noop}
            />
          </div>
        </ComponentDemo>

        <ComponentDemo label="Input with Icon" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <Input
              label="Search"
              icon="search"
              placeholder="Search customers..."
              value=""
              onChange={noop}
            />
          </div>
        </ComponentDemo>

        <ComponentDemo label="Disabled Input" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <Input
              label="Account ID"
              disabled
              value="ACC-12345"
              onChange={noop}
            />
          </div>
        </ComponentDemo>

        <CodeBlock>
          {`import { Input } from 'components/Form/Input';

// Basic input
<Input
  label="Full Name"
  placeholder="Enter your name"
  value={inputValue}
  onChange={(e) => setInputValue(e.target.value)}
/>

// With description
<Input
  label="Email Address"
  description="We'll never share your email"
  type="email"
  value={emailValue}
  onChange={(e) => setEmailValue(e.target.value)}
/>

// With error
<Input
  label="Username"
  error="This username is already taken"
  value={username}
  onChange={(e) => setUsername(e.target.value)}
/>

// With icon
<Input
  label="Search"
  icon="search"
  placeholder="Search..."
  value={searchValue}
  onChange={(e) => setSearchValue(e.target.value)}
/>

// Disabled
<Input
  label="Account ID"
  disabled
  value="ACC-12345"
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Select Dropdown (Legacy)</SectionTitle>
        <SectionDescription>
          {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
          <strong>⚠️ Legacy component - use Modern Select instead.</strong> This
          is the old dropdown select component from components/Form/Select. For
          new development, use the Modern Select component from FormFields
          below.
        </SectionDescription>

        <ComponentDemo label="Basic Select" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <Select
              label="Select a state"
              placeholder="Choose one..."
              options={[
                { value: 'ca', label: 'California' },
                { value: 'ny', label: 'New York' },
                { value: 'tx', label: 'Texas' },
                { value: 'fl', label: 'Florida' },
              ]}
              value={selectValue}
              onChange={(e) => setSelectValue(e.target.value)}
            />
          </div>
        </ComponentDemo>

        <ComponentDemo label="Select with Error" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <Select
              label="Select a country"
              error="Please select a country"
              options={[
                { value: 'us', label: 'United States' },
                { value: 'ca', label: 'Canada' },
                { value: 'uk', label: 'United Kingdom' },
              ]}
              value=""
              onChange={noop}
            />
          </div>
        </ComponentDemo>

        <ComponentDemo label="Minimal Variant" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <Select
              variant="minimal"
              options={[
                { value: 'newest', label: 'Newest First' },
                { value: 'oldest', label: 'Oldest First' },
                { value: 'name', label: 'By Name' },
              ]}
              value="newest"
              onChange={noop}
            />
          </div>
        </ComponentDemo>

        <CodeBlock>
          {`import { Select } from 'components/Form/Select';

// Basic select
<Select
  label="Select a state"
  placeholder="Choose one..."
  options={[
    { value: 'ca', label: 'California' },
    { value: 'ny', label: 'New York' },
    { value: 'tx', label: 'Texas' },
  ]}
  value={selectValue}
  onChange={(e) => setSelectValue(e.target.value)}
/>

// With error
<Select
  label="Select a country"
  error="Please select a country"
  options={countries}
  value={country}
  onChange={(e) => setCountry(e.target.value)}
/>

// Minimal variant (less padding, for inline use)
<Select
  variant="minimal"
  options={sortOptions}
  value={sortBy}
  onChange={(e) => setSortBy(e.target.value)}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Modern Select (Recommended)</SectionTitle>
        <SectionDescription>
          {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
          <strong>⭐ Use this component for new development.</strong> Modern
          select dropdown from FormFields with improved styling, dark mode
          support, and consistency with other form components. This is the
          preferred select component going forward.
        </SectionDescription>

        <ComponentDemo label="Basic Modern Select" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <ModernSelect
              label="Select Account"
              placeholder="-- Select an account --"
              options={[
                { value: 1, label: 'Speedway Racing' },
                { value: 2, label: 'Thunder Valley Track' },
                { value: 3, label: 'Mountain Peak Raceway' },
                { value: 4, label: 'Desert Storm Circuit' },
              ]}
              value={modernSelectValue}
              onChange={(e) => setModernSelectValue(e.target.value)}
            />
          </div>
        </ComponentDemo>

        <ComponentDemo label="With Required Field" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <ModernSelect
              label="Select Category"
              placeholder="-- Choose a category --"
              required
              options={[
                { value: 'racing', label: 'Racing Events' },
                { value: 'training', label: 'Training Sessions' },
                { value: 'exhibition', label: 'Exhibition' },
              ]}
              value=""
              onChange={noop}
            />
          </div>
        </ComponentDemo>

        <ComponentDemo label="With Error State" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <ModernSelect
              label="Select Payment Method"
              placeholder="-- Select payment method --"
              required
              error
              helperText="Payment method is required"
              options={[
                { value: 'card', label: 'Credit Card' },
                { value: 'ach', label: 'Bank Transfer (ACH)' },
                { value: 'cash', label: 'Cash' },
              ]}
              value=""
              onChange={noop}
            />
          </div>
        </ComponentDemo>

        <ComponentDemo label="With Helper Text" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <ModernSelect
              label="Event Type"
              helperText="Select the type of event you want to create"
              options={[
                { value: 'race', label: 'Race Event' },
                { value: 'practice', label: 'Practice Day' },
                { value: 'special', label: 'Special Event' },
              ]}
              value="race"
              onChange={noop}
            />
          </div>
        </ComponentDemo>

        <ComponentDemo label="Custom Width" align="stretch">
          <div style={{ display: 'flex', gap: '16px' }}>
            <ModernSelect
              label="Small"
              width="150px"
              options={[
                { value: '1', label: 'Option 1' },
                { value: '2', label: 'Option 2' },
              ]}
              value="1"
              onChange={noop}
            />
            <ModernSelect
              label="Medium"
              width="250px"
              options={[
                { value: '1', label: 'Option 1' },
                { value: '2', label: 'Option 2' },
              ]}
              value="1"
              onChange={noop}
            />
          </div>
        </ComponentDemo>

        <CodeBlock>
          {`import { Select } from 'components/FormFields';

// Basic select
<Select
  label="Select Account"
  placeholder="-- Select an account --"
  options={[
    { value: 1, label: 'Speedway Racing' },
    { value: 2, label: 'Thunder Valley Track' },
  ]}
  value={selectedAccountId}
  onChange={(e) => setSelectedAccountId(e.target.value)}
/>

// With required field
<Select
  label="Select Category"
  placeholder="-- Choose a category --"
  required
  options={categories}
  value={category}
  onChange={(e) => setCategory(e.target.value)}
/>

// With error state
<Select
  label="Payment Method"
  required
  error
  helperText="Payment method is required"
  options={paymentMethods}
  value={paymentMethod}
  onChange={(e) => setPaymentMethod(e.target.value)}
/>

// With helper text
<Select
  label="Event Type"
  helperText="Select the type of event you want to create"
  options={eventTypes}
  value={eventType}
  onChange={(e) => setEventType(e.target.value)}
/>

// Custom width
<Select
  label="Small Select"
  width="150px"
  options={options}
  value={value}
  onChange={handleChange}
/>`}
        </CodeBlock>

        <PropsTable
          props={[
            {
              name: 'label',
              type: 'string',
              required: false,
              description: 'Label text displayed above the select',
            },
            {
              name: 'required',
              type: 'boolean',
              default: 'false',
              required: false,
              description: 'Shows a red asterisk (*) next to the label',
            },
            {
              name: 'value',
              type: 'string | number',
              required: false,
              description: 'Currently selected value',
            },
            {
              name: 'onChange',
              type: 'function',
              required: false,
              description: 'Change handler function (receives event)',
            },
            {
              name: 'placeholder',
              type: 'string',
              required: false,
              description: 'Placeholder text shown when no value is selected',
            },
            {
              name: 'options',
              type: 'array',
              default: '[]',
              required: false,
              description:
                'Array of {value, label} objects for the dropdown options',
            },
            {
              name: 'helperText',
              type: 'string',
              required: false,
              description: 'Helper text displayed below the select',
            },
            {
              name: 'error',
              type: 'boolean',
              default: 'false',
              required: false,
              description: 'Shows error state styling',
            },
            {
              name: 'width',
              type: 'string',
              default: "'100%'",
              required: false,
              description: 'Width of the select field (e.g., "200px", "50%")',
            },
            {
              name: 'maxWidth',
              type: 'string',
              required: false,
              description: 'Maximum width of the select field',
            },
          ]}
        />
      </Section>

      <Section>
        <SectionTitle>Search Input</SectionTitle>
        <SectionDescription>
          Specialized search input with preset styling and search icon. Used for
          filtering and searching throughout the application.
        </SectionDescription>

        <ComponentDemo label="Search Input" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <SearchInput
              placeholder="Search customers..."
              handleChange={(e) => setSearchValue(e.target.value)}
              handleBlur={noop}
              handleKeyPress={noop}
            />
          </div>
        </ComponentDemo>

        <CodeBlock>
          {`import { SearchInput } from 'components/Form/SearchInput';

<SearchInput
  placeholder="Search customers..."
  handleChange={(e) => setSearchValue(e.target.value)}
  handleBlur={handleBlur}
  handleKeyPress={handleKeyPress}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Phone Input</SectionTitle>
        <SectionDescription>
          A specialized input component for phone numbers with country code
          selector. Automatically formats phone numbers based on the selected
          country.
        </SectionDescription>

        <ComponentDemo
          label="Phone Input"
          direction="column"
          gap="16px"
          align="stretch"
        >
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <PhoneInput
              label="Phone Number"
              value={phoneValue}
              onChange={setPhoneValue}
              countryCode={countryCode}
              onCountryChange={setCountryCode}
            />
          </div>
        </ComponentDemo>

        <CodeBlock>
          {`import { PhoneInput } from 'components/FormFields';

const [phoneValue, setPhoneValue] = useState('');
const [countryCode, setCountryCode] = useState('+1');

<PhoneInput
  label="Phone Number"
  value={phoneValue}
  onChange={setPhoneValue}
  countryCode={countryCode}
  onCountryChange={setCountryCode}
/>`}
        </CodeBlock>

        <PropsTable
          props={[
            {
              name: 'label',
              type: 'string',
              required: false,
              default: "'Phone Number'",
              description: 'Label text displayed above the input',
            },
            {
              name: 'value',
              type: 'string',
              required: true,
              description: 'The phone number value (without country code)',
            },
            {
              name: 'onChange',
              type: 'function',
              required: true,
              description:
                'Callback when phone number changes. Receives formatted phone string.',
            },
            {
              name: 'countryCode',
              type: 'string',
              required: true,
              default: "'+1'",
              description: "Selected country code (e.g., '+1', '+44')",
            },
            {
              name: 'onCountryChange',
              type: 'function',
              required: true,
              description:
                'Callback when country code changes. Receives country code string.',
            },
            {
              name: 'placeholder',
              type: 'string',
              required: false,
              description:
                'Custom placeholder text (auto-generated based on country if not provided)',
            },
          ]}
        />
      </Section>

      <Section>
        <SectionTitle>Textarea</SectionTitle>
        <SectionDescription>
          A multi-line text input component with customizable row count.
          Supports labels, helper text, error states, and required field
          indicators.
        </SectionDescription>

        <ComponentDemo label="Basic Textarea" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <Textarea
              label="Description"
              placeholder="Enter a description..."
              value={textareaValue}
              onChange={(e) => setTextareaValue(e.target.value)}
              rows={4}
            />
          </div>
        </ComponentDemo>

        <ComponentDemo label="Textarea with Error" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <Textarea
              label="Notes"
              required
              placeholder="Enter notes..."
              value=""
              onChange={noop}
              error
              helperText="Notes are required"
              rows={3}
            />
          </div>
        </ComponentDemo>

        <ComponentDemo label="Textarea with Helper Text" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <Textarea
              label="Bio"
              placeholder="Tell us about yourself..."
              value="Sample bio text"
              onChange={noop}
              helperText="Maximum 500 characters"
              rows={3}
            />
          </div>
        </ComponentDemo>

        <CodeBlock>
          {`import { Textarea } from 'components/FormFields';

// Basic textarea
<Textarea
  label="Description"
  placeholder="Enter a description..."
  value={description}
  onChange={(e) => setDescription(e.target.value)}
  rows={4}
/>

// With error state
<Textarea
  label="Notes"
  required
  value={notes}
  onChange={(e) => setNotes(e.target.value)}
  error
  helperText="Notes are required"
/>

// With helper text
<Textarea
  label="Bio"
  placeholder="Tell us about yourself..."
  value={bio}
  onChange={(e) => setBio(e.target.value)}
  helperText="Maximum 500 characters"
  rows={3}
/>`}
        </CodeBlock>

        <PropsTable
          props={[
            {
              name: 'label',
              type: 'string',
              required: false,
              description: 'Label text displayed above the textarea',
            },
            {
              name: 'required',
              type: 'boolean',
              default: 'false',
              required: false,
              description: 'Shows a red asterisk (*) next to the label',
            },
            {
              name: 'value',
              type: 'string',
              required: true,
              description: 'The current textarea value',
            },
            {
              name: 'onChange',
              type: 'function',
              required: true,
              description: 'Change handler function (receives event)',
            },
            {
              name: 'placeholder',
              type: 'string',
              required: false,
              description: 'Placeholder text shown when empty',
            },
            {
              name: 'rows',
              type: 'number',
              default: '3',
              required: false,
              description: 'Number of visible text rows',
            },
            {
              name: 'helperText',
              type: 'string',
              required: false,
              description: 'Helper or error text displayed below the textarea',
            },
            {
              name: 'error',
              type: 'boolean',
              default: 'false',
              required: false,
              description: 'Shows error state styling',
            },
          ]}
        />
      </Section>

      <Section>
        <SectionTitle>Toggle</SectionTitle>
        <SectionDescription>
          A toggle switch component for boolean values. Supports static labels,
          dynamic checked/unchecked labels, and disabled state.
        </SectionDescription>

        <ComponentDemo label="Basic Toggle" align="stretch">
          <div
            style={{
              width: '400px',
              maxWidth: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <Toggle
              label="Enable notifications"
              checked={toggleChecked}
              onChange={(e) => setToggleChecked(e.target.checked)}
            />
            <Toggle
              checked={toggleChecked2}
              onChange={(e) => setToggleChecked2(e.target.checked)}
              checkedLabel="Active"
              uncheckedLabel="Inactive"
            />
            <Toggle
              label="Disabled toggle"
              checked={false}
              onChange={noop}
              disabled
            />
          </div>
        </ComponentDemo>

        <CodeBlock>
          {`import { Toggle } from 'components/FormFields';

// With static label
<Toggle
  label="Enable notifications"
  checked={enabled}
  onChange={(e) => setEnabled(e.target.checked)}
/>

// With dynamic checked/unchecked labels
<Toggle
  checked={isActive}
  onChange={(e) => setIsActive(e.target.checked)}
  checkedLabel="Active"
  uncheckedLabel="Inactive"
/>

// Disabled
<Toggle
  label="Locked setting"
  checked={false}
  onChange={noop}
  disabled
/>`}
        </CodeBlock>

        <PropsTable
          props={[
            {
              name: 'checked',
              type: 'boolean',
              required: true,
              description: 'Whether the toggle is checked/on',
            },
            {
              name: 'onChange',
              type: 'function',
              required: true,
              description:
                'Change handler (receives event with e.target.checked)',
            },
            {
              name: 'label',
              type: 'string',
              required: false,
              description: 'Static label text (always shown)',
            },
            {
              name: 'checkedLabel',
              type: 'string',
              required: false,
              description: 'Label shown when toggle is checked',
            },
            {
              name: 'uncheckedLabel',
              type: 'string',
              required: false,
              description: 'Label shown when toggle is unchecked',
            },
            {
              name: 'disabled',
              type: 'boolean',
              default: 'false',
              required: false,
              description: 'Disables the toggle',
            },
          ]}
        />
      </Section>

      <Section>
        <SectionTitle>Form Utilities</SectionTitle>
        <SectionDescription>
          Helper components for building form layouts: FormField for spacing,
          FormLabel for labels, and Tooltip for hover hints.
        </SectionDescription>

        <ComponentDemo label="FormField & FormLabel" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <FormField>
              <FormLabel>Custom Label</FormLabel>
              <input
                type="text"
                placeholder="Field inside FormField wrapper"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
            </FormField>
          </div>
        </ComponentDemo>

        <ComponentDemo label="Tooltip" align="stretch">
          <div
            style={{
              width: '400px',
              maxWidth: '100%',
              display: 'flex',
              gap: '16px',
            }}
          >
            <Tooltip text="This is a helpful tooltip">
              <span
                style={{
                  padding: '8px 16px',
                  background: '#f1f5f9',
                  borderRadius: '6px',
                  cursor: 'help',
                  fontSize: '14px',
                }}
              >
                Hover me
              </span>
            </Tooltip>
          </div>
        </ComponentDemo>

        <CodeBlock>
          {`import { FormField, FormLabel, Tooltip } from 'components/FormFields';

// FormField wrapper for consistent spacing
<FormField>
  <Input label="Name" value={name} onChange={handleChange} />
</FormField>

// Full-width FormField (spans all grid columns)
<FormField fullWidth>
  <Textarea label="Description" value={desc} onChange={handleChange} />
</FormField>

// FormLabel for custom labels
<FormField>
  <FormLabel>Custom Field</FormLabel>
  <CustomInput />
</FormField>

// Tooltip for hover hints
<Tooltip text="This field is required for processing">
  <FormLabel>Processing ID</FormLabel>
</Tooltip>`}
        </CodeBlock>

        <PropsTable
          props={[
            {
              name: 'FormField: children',
              type: 'node',
              required: true,
              description: 'The form field content to wrap',
            },
            {
              name: 'FormField: fullWidth',
              type: 'boolean',
              default: 'false',
              required: false,
              description: 'Whether the field should span all grid columns',
            },
            {
              name: 'FormLabel: children',
              type: 'node',
              required: true,
              description: 'The label text/content',
            },
            {
              name: 'Tooltip: text',
              type: 'string',
              required: true,
              description: 'The tooltip text to display on hover',
            },
            {
              name: 'Tooltip: children',
              type: 'node',
              required: true,
              description: 'The element to wrap with the tooltip',
            },
          ]}
        />
      </Section>

      <Section>
        <SectionTitle>Input Types</SectionTitle>
        <SectionDescription>
          The Input component supports all standard HTML input types.
        </SectionDescription>

        <ComponentDemo
          label="Different Input Types"
          direction="column"
          gap="16px"
          align="stretch"
        >
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <Input
              label="Text Input"
              type="text"
              placeholder="Enter text"
              value=""
              onChange={noop}
            />
          </div>
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <Input
              label="Email Input"
              type="email"
              placeholder="email@example.com"
              value=""
              onChange={noop}
            />
          </div>
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <Input
              label="Password Input"
              type="password"
              placeholder="Enter password"
              value=""
              onChange={noop}
            />
          </div>
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <Input
              label="Number Input"
              type="number"
              placeholder="0"
              value=""
              onChange={noop}
            />
          </div>
        </ComponentDemo>

        <CodeBlock>
          {`// Different input types
<Input type="text" label="Text" />
<Input type="email" label="Email" />
<Input type="password" label="Password" />
<Input type="number" label="Number" />
<Input type="tel" label="Phone" />
<Input type="url" label="URL" />
<Input type="date" label="Date" />`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Input Props</SectionTitle>
        <PropsTable props={inputProps} />
      </Section>

      <Section>
        <SectionTitle>Select Props</SectionTitle>
        <PropsTable props={selectProps} />
      </Section>

      <Section>
        <SectionTitle>Form Layout Examples</SectionTitle>
        <SectionDescription>
          Common patterns for laying out form fields.
        </SectionDescription>

        <ComponentDemo label="Stacked Form Fields" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <Input
              label="First Name"
              placeholder="John"
              value=""
              onChange={noop}
            />
            <div style={{ marginTop: '16px' }}>
              <Input
                label="Last Name"
                placeholder="Doe"
                value=""
                onChange={noop}
              />
            </div>
            <div style={{ marginTop: '16px' }}>
              <Input
                label="Email"
                type="email"
                placeholder="john@example.com"
                value=""
                onChange={noop}
              />
            </div>
            <div style={{ marginTop: '16px' }}>
              <Select
                label="Country"
                placeholder="Select country"
                options={[
                  { value: 'us', label: 'United States' },
                  { value: 'ca', label: 'Canada' },
                ]}
                value=""
                onChange={noop}
              />
            </div>
          </div>
        </ComponentDemo>

        <CodeBlock>
          {`// Stacked form layout
<div>
  <Input
    label="First Name"
    value={firstName}
    onChange={(e) => setFirstName(e.target.value)}
  />

  <div style={{ marginTop: '16px' }}>
    <Input
      label="Last Name"
      value={lastName}
      onChange={(e) => setLastName(e.target.value)}
    />
  </div>

  <div style={{ marginTop: '16px' }}>
    <Select
      label="Country"
      options={countries}
      value={country}
      onChange={(e) => setCountry(e.target.value)}
    />
  </div>
</div>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Best Practices</SectionTitle>
        <ComponentDemo direction="column" gap="16px">
          <div
            style={{ fontSize: '14px', lineHeight: '1.8', color: 'inherit' }}
          >
            {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
            <strong>✅ Do:</strong>
            <br />
            • Use the modern FormFields Select component for new development
            <br />
            • Always provide clear, descriptive labels
            <br />
            • Use appropriate input types for better mobile keyboards
            <br />
            • Show helpful error messages when validation fails
            <br />
            • Use descriptions for complex fields that need explanation
            <br />
            • Disable fields that users shouldn't interact with
            <br />
            • Use placeholder text to show format examples
            <br />
            • Test components in both light and dark themes
            <br />
            <br />
            {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
            <strong>❌ Don't:</strong>
            <br />
            • Don't use the old components/Form/Select - use
            components/FormFields/Select instead
            <br />
            • Don't use placeholder as a replacement for labels
            <br />
            • Don't make error messages vague or technical
            <br />
            • Don't forget to handle keyboard events (Enter to submit, etc.)
            <br />
            • Don't use disabled when you mean readonly
            <br />• Don't forget to provide onChange handlers
          </div>
        </ComponentDemo>
      </Section>
    </div>
  );
};

export default Forms;
